import React, { useEffect } from 'react';
import { TouchableOpacity, View } from 'react-native';
import {
  Button,
  DefaultScreenContainer,
  InsuranceCardBenefit,
  OfferBanner,
  Text,
} from '~/components';
import useAppNavigation from '~/hooks/useAppNavigation';
import { isWeb } from '~/utils/device';

import MoneyBillSVG from '~/assets/svgs/money-bill.svg';
import PinSVG from '~/assets/svgs/pin.svg';
import DrugStoreSVG from '~/assets/svgs/drugstore.svg';
import CallCenterSVG from '~/assets/svgs/call-center.svg';
import FacilitySVG from '~/assets/svgs/facility.svg';
import ANSLogoSVG from '~/assets/svgs/ans-logo.svg';

import BannerImg from '~/assets/img/banners/odonto-protection-banner.png';
import BannerImgDesk from '~/assets/img/banners/odonto-protection-desk.png';
import type { Benefit } from '~/components/InsuranceCardBenefit';
import { journeyLogUseCase } from '~/useCases/PSF/JourneyLogUseCase';
import { useUserInfoStore } from '~/stores/userInfo';
import GeneralInfoFooter from '../components/GeneralInfoFooter';
import { useSalesFunnel } from '~/stores';
import { MarisaAppUtils } from '~/utils/MarisaAppUtils';

const INSURANCE_TYPE = 'odonto-protection';

const BENEFITS: Benefit[] = [
  {
    icon: MoneyBillSVG,
    title: 'Valor fixo mensal',
    description: (
      <Text className="text-sm mt-0">
        Por apenas <Text className="text-primary-600">R$ 37,90 por mês</Text>,
        você conta com profissionais experientes pra cuidar da sua saúde bucal e
        possibilidade de inclusão de dependentes, sendo familiares ou não.
      </Text>
    ),
  },
  {
    icon: PinSVG,
    title: 'Rede credenciada',
    description: (
      <Text className="text-sm">
        Ampla Rede Credenciada em todo o Brasil, com mais de{' '}
        <Text className="text-primary-600">38 mil consultórios</Text>{' '}
        credenciados. Permite inclusão de dependentes.
      </Text>
    ),
  },
  {
    icon: DrugStoreSVG,
    title: 'Farmácia',
    description: (
      <Text className="text-sm">
        Descontos de até 70% em medicamentos, com diversas parcerias de
        farmácias.
      </Text>
    ),
  },
  {
    icon: CallCenterSVG,
    title: 'Urgente e emergência',
    description: (
      <Text className="text-sm">
        Em 24h após adesão (alívio de dor e medicamento)
      </Text>
    ),
  },
  {
    icon: FacilitySVG,
    title: 'Facilidade',
    description: (
      <Text className="text-sm">
        Sem pré-análise e sem restrição de idade e sem coparticipação
      </Text>
    ),
  },
];

const OdontoProtectionDetails: React.FC = () => {
  const { goBack } = useAppNavigation();
  const { init } = useSalesFunnel();

  const id = useUserInfoStore((state) => state.user?.id);

  useEffect(() => {
    init('odonto-protection');
    if (id) {
      journeyLogUseCase(useUserInfoStore.getState().user, 'ODONTO');
    }
  }, [id, init]);

  return (
    <DefaultScreenContainer>
      <OfferBanner
        hireInsurance={{
          showButton: true,
          type: INSURANCE_TYPE,
          label: 'Contrate em nossas lojas',
          onPressButton: MarisaAppUtils.goToStoresList,
        }}
        images={{
          mobile: BannerImg,
          web: { banner: BannerImgDesk },
        }}
        insuranceName="Plano Odonto"
        insuranceDetail="Complete o seu look com um sorriso radiante!"
      />

      <View className="px-6">
        <View className="md:items-center md:justify-center items-start justify-start">
          <Text className="mt-6 sm:mt-16 text-gray-700 text-[22px]">
            O que é o Plano Odonto?
          </Text>

          <Text className="mt-4 text-gray-400 md:text-center text-[16px] leading-6">
            É um serviço de assistência odontológica que oferece cobertura para
            cuidados dentários, incluindo consultas, exames, tratamentos e
            procedimentos relacionados à saúde bucal.
          </Text>
        </View>

        {isWeb && (
          <View className="mt-10 mb-2 bg-gray-100 h-[1px] invisible md:visible" />
        )}

        <Text className="mt-8 text-xl text-gray-700 md:items-center md:justify-center md:text-center md:mb-6">
          Conheça os benefícios:
        </Text>

        <View className="lg:flex-row lg:justify-between">
          {BENEFITS.map((benefit, index) => (
            <InsuranceCardBenefit data={benefit} type="health" key={index} />
          ))}
        </View>

        <View className="items-center justify-center py-8 bg-feedback-neutral-bkg rounded-lg mt-8 mb-7">
          <Text className="text-secondary-800 text-sm">
            Tudo isso por R$ <Text className="text-xl font-medium">37,90</Text>{' '}
            por pessoas/mês
          </Text>
        </View>

        <Text className="text-center text-[12px] text-gray-400 mb-8">
          Exclusivo Cartão Marisa
        </Text>

        <View className="items-center mb-8">
          <ANSLogoSVG />
        </View>
        <View className="py-4 px-6 items-center justify-center lg:mx-auto lg:w-[328px]">
          <Button className="flex-1" onPress={MarisaAppUtils.goToStoresList}>
            Contrate em nossas lojas
          </Button>
        </View>
        <GeneralInfoFooter className="mt-12" />
      </View>

      <View className="py-4 px-6 items-center justify-center lg:mx-auto lg:w-[328px]">
        {isWeb && (
          <TouchableOpacity onPress={() => goBack()}>
            <Text className="text-primary-600 uppercase font-medium mt-4 text-xs">
              Voltar
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </DefaultScreenContainer>
  );
};

export default OdontoProtectionDetails;
